import { GridRowId } from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';

/**
 * Safely put a grid cell into edit mode.
 *
 * Opening a cell for editing is a non-critical UX convenience that often runs
 * on a deferred tick (e.g. inside `onRowsCountChange`/`onRowClick` or a
 * `setTimeout`). By the time it runs the grid may have re-rendered, the row may
 * be gone, or the cell may already be in edit mode (e.g. a double-click
 * scheduling two of these) — all of which make MUI's `startCellEditMode` throw
 * `MUI X: The cell with id=... and field=... is not in view mode.` and crash
 * the app (WA-3618).
 *
 * This guards on the live cell mode / row existence and swallows the residual
 * race so a failure to open the editor is never fatal.
 */
export const safeStartCellEditMode = (
  gridApi: GridApiPremium | null | undefined,
  id: GridRowId,
  field: string,
): void => {
  try {
    if (!gridApi?.getRow?.(id)) return;
    if (gridApi.getCellMode(id, field) !== 'view') return;
    gridApi.startCellEditMode({ id, field });
  } catch {
    /* grid re-rendered / cell no longer editable — non-fatal, ignore */
  }
};
